// src/pages/onboarding/benefits.tsx
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  Clock,
  Shield,
  Star,
  Users,
  BarChart3,
  Zap,
  Lock,
  TrendingUp,
  Lightbulb,
  Database,
  History,
} from "lucide-react";

const benefits = [
  {
    icon: Database,
    gradient: "from-blue-500 to-cyan-500",
    title: "Save Your Connected Accounts & API Keys Securely",
    description:
      "Never lose your setup again. Your Google Ads connections and API configurations are stored safely and ready whenever you return.",
  },
  {
    icon: History,
    gradient: "from-purple-500 to-pink-500",
    title: "Track Your AI Usage & Query History Over Time",
    description:
      "See all your past analyses, compare results over time, and build a comprehensive view of your account optimization journey.",
  },
  {
    icon: Zap,
    gradient: "from-emerald-500 to-teal-500",
    title: "Access Exclusive Features & Advanced Tools",
    description:
      "Unlock member-only features, advanced analytics, and cutting-edge optimization tools that give you a competitive edge.",
  },
  {
    icon: TrendingUp,
    gradient: "from-amber-500 to-orange-500",
    title: "Receive Updates on New Strategies & Platform Features",
    description:
      "Stay ahead of the curve with insider tips, new Google Ads features, and proven strategies from our team of experts.",
  },
];

const testimonials = [
  {
    name: "Sarah Mitchell",
    role: "E-commerce Director",
    text: "I love being able to white-label the tool. My clients are so impressed, they think I built this!.",
  },
  {
    name: "David Park",
    role: "Agency Founder",
    text: "My whol team LOVE this tool. The insights, the data-viz & of course the time savings.",
  },
  {
    name: "Maria Rodriguez",
    role: "Marketing Manager",
    text: "I love being able to pick up exactly where I left off. No more re-entering my API keys or losing my progress.",
  },
];

export default function OnboardingBenefitsPage() {
  return (
    <div className="min-h-screen bg-gradient-hero relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float" />
        <div
          className="absolute bottom-0 right-1/4 w-80 h-80 bg-secondary/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute top-1/2 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl animate-float"
          style={{ animationDelay: "2s" }}
        />
      </div>

      <div className="relative z-10 px-6 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-primary-light/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-primary/10">
              <Star className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium text-sm">
                Join 2,742+ smart advertisers
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Ready to
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                {" "}
                Unlock the Full Power
              </span>
              <br />
              of 8020agent?
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Create a free account to save your progress, access advanced
              features, and never lose your optimization insights again. Join
              thousands of advertisers who are staying ahead of the competition.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-16 items-start">
            {/* Left Content: Benefits */}
            <div className="lg:col-span-7 space-y-8">
              <div className="grid gap-6">
                {benefits.map(
                  ({ icon: Icon, gradient, title, description }, index) => (
                    <div
                      key={title}
                      className="group flex items-center gap-6 animate-scale-in"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div
                        className={`flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-r ${gradient} flex items-center justify-center shadow-medium group-hover:scale-110 transition-transform duration-300`}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </div>

                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors mb-2">
                          {title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed">
                          {description}
                        </p>
                      </div>
                    </div>
                  ),
                )}
              </div>

              {/* Trust Indicators */}
              <div
                className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground animate-fade-in"
                style={{ animationDelay: "600ms" }}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-success" />
                  <span>100% Free to start</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-success" />
                  <span>Setup in 30 seconds</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-success" />
                  <span>Your data stays secure</span>
                </div>
              </div>

              {/* CTA Button - Centered */}
              <div
                className="animate-fade-in flex justify-center"
                style={{ animationDelay: "400ms" }}
              >
                <Button
                  asChild
                  variant="default"
                  size="big"
                  className="w-full sm:w-auto"
                >
                  <Link to="/sign-up">Create Free Account</Link>
                </Button>
              </div>
            </div>

            {/* Right Side: Testimonials */}
            <div className="lg:col-span-5 space-y-6">
              <div
                className="animate-fade-in"
                style={{ animationDelay: "500ms" }}
              >
                <h3 className="text-2xl font-semibold text-foreground mb-6 text-center">
                  What Happy Users Say
                </h3>

                <div className="space-y-4">
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={testimonial.name}
                      className="relative p-6 rounded-2xl bg-gradient-card backdrop-blur-sm border border-border/50 shadow-soft hover:shadow-medium transition-all duration-300 animate-scale-in"
                      style={{ animationDelay: `${600 + index * 100}ms` }}
                    >
                      <div className="flex items-start gap-1 mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-4 h-4 text-amber-400 fill-current"
                          />
                        ))}
                      </div>
                      <p className="text-muted-foreground mb-4 leading-relaxed">
                        "{testimonial.text}"
                      </p>
                      <div>
                        <p className="font-medium text-foreground">
                          {testimonial.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div
            className="text-center mt-16 animate-fade-in"
            style={{ animationDelay: "1000ms" }}
          >
            <div className="max-w-2xl mx-auto">
              <div className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Lock className="w-4 h-4 text-success" />
                  <span className="font-medium text-foreground">
                    Your Privacy is Protected
                  </span>
                </div>
                <p className="leading-relaxed">
                  When you create a free account, your data is stored securely
                  and you'll be able to access your analysis history anytime.
                  Your Google Ads data remains in your own Google Sheet - we
                  never store your advertising data on our servers.
                </p>
              </div>

              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link
                  to="/sign-in"
                  className="text-primary hover:text-primary-dark underline underline-offset-2 transition-colors"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
